import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { FamilyMemberPayload } from '../../../../../../common';

export default function useIntakeFamilyMembers() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const familyMembers = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.family?.family_members ?? [],
  );
  const isCreating = useSelector(
    (state: RootState) => state.intakeQuestionnaire.createFamilyMemberLoading,
  );
  const isSaving = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.updateFamilyMemberLoading ||
      state.intakeQuestionnaire.deleteFamilyMemberLoading,
  );

  const create = useCallback(
    async (data: FamilyMemberPayload) => {
      if (!intakeQuestionnaireId) return undefined;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeFamilyMember({
          intakeQuestionnaireId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeFamilyMember.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const update = useCallback(
    async (memberId: number, data: FamilyMemberPayload) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.updateIntakeFamilyMember({
          intakeQuestionnaireId,
          memberId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.updateIntakeFamilyMember.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const remove = useCallback(
    async (memberId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeFamilyMember({
          intakeQuestionnaireId,
          memberId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeFamilyMember.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return { familyMembers, isCreating, isSaving, create, update, remove };
}
